import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  Skeleton,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Refresh,
  AttachMoney,
  Category,
  Business,
  TrendingUp,
  Warning,
  CheckCircle,
  OpenInNew,
  CloudDownload,
  Assignment,
  ExpandMore,
  ContentCopy,
  PlayArrow,
  LocalOffer,
  Schedule,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { MapPricingRecord } from '../services/mapPricingApi';
import { integrationApiService } from '../services/integrationApi';
import { auth } from '../firebase';
import { getApiUrl } from '../utils/apiConfig';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { ingestGapsToCsv } from '../utils/ingestGapCsv';
import { BrandSelect, ChipInput, FormDialog, LoadingState, NumberField, PageContainer, PageHeader, StatusChip, StatusTone, statusTone, statusLabel } from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';
import { formatCurrency, formatNumber } from '../i18n';
import { validateUrl } from '../utils/inputValidators';
import { parseProductUrls } from '../components/dataIngestion/BrandTriggerDialog';

function splitProductUrls(text: string): string[] {
  return text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

interface MapPricingStats {
  totalRecords: number;
  activeRecords: number;
  upcomingRecords?: number;
  changedRecords: number;
  newRecords: number;
  deletedRecords: number;
  averagePriceChange: number;
  priceIncreaseCount: number;
  priceDecreaseCount: number;
  modelsAffected: number;
}

interface IngestGapBrandGroup {
  brand: string;
  brandId: string | null;
  skuCount: number;
  cameraSkuCount?: number;
  skus: Array<{
    pricingId: string;
    model: string;
    description?: string;
    brand: string;
    mapPrice?: number;
    instantRebate?: number;
    pricingSource?: string;
    status?: string;
    productStatus: 'placeholder' | 'missing';
    productType?: 'camera' | 'lens' | 'accessory';
  }>;
  scraperImplemented: boolean;
  scraperEnabled: boolean;
  recommendedAction: 'queue_discovery' | 'enable_scraper' | 'file_scraper_ticket';
  ticketDraft: { title: string; body: string };
}

interface BundleRebateBrandGroup {
  brand: string;
  brandId: string | null;
  skuCount: number;
  skus: Array<{
    pricingId: string;
    model: string;
    brand: string;
    mapPrice?: number;
    instantRebate?: number;
    status?: string;
    offerProduct?: string;
    condition?: string;
  }>;
  ticketDraft: { title: string; body: string };
}

interface IngestGapReport {
  generatedAt: string;
  totalPricingRecordsScanned: number;
  truncated?: boolean;
  totalNeedingIngest: number;
  totalMissingCameras: number;
  totalBundleRebates: number;
  brandsWithScraperReady: number;
  brandsNeedingScraper: number;
  groups: IngestGapBrandGroup[];
  missingCameraGroups: IngestGapBrandGroup[];
  bundleRebateGroups: BundleRebateBrandGroup[];
  bundleTicketDraft: { title: string; body: string };
  aiBriefMarkdown: string;
}

type GapFilter = '' | 'needs_ingest' | 'missing_cameras' | 'bundle_rebates';
type GapDialogTab = 'catalog' | 'cameras' | 'bundles';

export const AdminMapPricingManagementPage: React.FC = () => {
  const theme = useTheme();
  const gapDialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [gapFilter, setGapFilter] = useState<GapFilter>('');
  const [gapDialogOpen, setGapDialogOpen] = useState(false);
  const [gapDialogTab, setGapDialogTab] = useState<GapDialogTab>('cameras');
  const [selectedGapBrands, setSelectedGapBrands] = useState<string[]>([]);
  const [gapUrlDrafts, setGapUrlDrafts] = useState<Record<string, string>>({});
  const [selectedRecord, setSelectedRecord] = useState<MapPricingRecord | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'edit' | 'delete'>('create');
  const { showToast } = useToast();
  const [exportingGaps, setExportingGaps] = useState(false);
  const [formData, setFormData] = useState({
    model: '',
    description: '',
    brand: '',
    category: '',
    mapPrice: '',
    surePrice: '',
    sppgPrice: '',
    status: 'active',
  });

  // Helper function to get auth token
  const getAuthToken = async (): Promise<string> => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }
    const token = await user.getIdToken(true);
    return token;
  };

  // Fetch MAP pricing statistics
  const { data: stats, isLoading: statsLoading } = useQuery<MapPricingStats>({
    queryKey: ['mapPricingStats'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/map-pricing/stats`, {
        headers: {
          'Authorization': `Bearer ${await getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch MAP pricing stats');
      const result = await response.json();
      return result.data;
    },
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [brandFilter, categoryFilter, rowsPerPage, gapFilter]);

  // Fetch MAP pricing records
  const { data: recordsData, isLoading: recordsLoading, refetch } = useQuery({
    queryKey: ['mapPricingRecords', page, rowsPerPage, debouncedSearch, brandFilter, categoryFilter, gapFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: rowsPerPage.toString(),
        offset: (page * rowsPerPage).toString(),
      });
      if (debouncedSearch) params.set('model', debouncedSearch);
      if (brandFilter) params.set('brand', brandFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (gapFilter === 'needs_ingest' || gapFilter === 'missing_cameras') {
        params.set('needsIngestion', 'true');
      }
      if (gapFilter === 'missing_cameras') params.set('productType', 'camera');
      if (gapFilter === 'bundle_rebates') params.set('gapKind', 'bundle_rebate');

      const response = await fetch(`${getApiUrl()}/map-pricing?${params}`, {
        headers: {
          'Authorization': `Bearer ${await getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch MAP pricing records');
      const result = await response.json();
      return result;
    },
  });

  const {
    data: gapReport,
    isLoading: gapReportLoading,
    refetch: refetchGapReport,
    isFetching: gapReportFetching,
  } = useQuery<IngestGapReport>({
    queryKey: ['map-pricing-ingest-gaps'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/map-pricing/ingest-gaps`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to build ingest-gap report');
      const result = await response.json();
      return result.data;
    },
    enabled: gapDialogOpen,
  });

  const queueDiscoveryMutation = useMutation({
    mutationFn: async (brandIds: string[]) => {
      const response = await fetch(`${getApiUrl()}/map-pricing/ingest-gaps/queue-discovery`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ brandIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(err, 'Failed to queue discovery scrapes', response.status));
      }
      return response.json();
    },
    onSuccess: (result) => {
      const results = result?.data?.results || [];
      const ok = results.filter((r: any) => r.jobId).length;
      showToast(`Queued discovery for ${ok} brand(s)`, 'success');
      setSelectedGapBrands([]);
    },
    onError: (error: Error) => {
      showToast(error.message, 'error');
    },
  });

  const downloadMissingProductsCsv = async () => {
    setExportingGaps(true);
    try {
      const params = new URLSearchParams();
      if (brandFilter) params.set('brand', brandFilter);
      params.set('limitPerBrand', '10000');
      const response = await fetch(`${getApiUrl()}/map-pricing/ingest-gaps?${params}`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(err, 'Failed to export missing products', response.status));
      }
      const result = await response.json();
      if (!result?.data?.groups) {
        throw new Error('Failed to export missing products');
      }
      const csv = ingestGapsToCsv(result.data);
      const blob = new Blob([csv], { type: 'text/csv; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `missing-products-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      showToast(message && message !== '[object Object]' ? message : 'CSV export failed', 'error');
    } finally {
      setExportingGaps(false);
    }
  };

  const scrapeGapUrlsMutation = useMutation({
    mutationFn: async ({ brandId, urls }: { brandId: string; urls: string[] }) => {
      return integrationApiService.triggerIngestion(brandId, {
        type: 'full_scrape',
        hostImages: true,
        urls,
        region: 'US',
      });
    },
    onSuccess: (data, vars) => {
      showToast(`Queued scrape of ${vars.urls.length} URL(s) for ${vars.brandId}${data?.jobId ? ` (${data.jobId})` : ''}`, 'success');
      setGapUrlDrafts((prev) => ({ ...prev, [vars.brandId]: '' }));
    },
    onError: (error: Error) => {
      showToast(error.message, 'error');
    },
  });

  const copyText = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(successMessage, 'success');
    } catch {
      showToast('Could not copy to clipboard', 'error');
    }
  };

  const actionLabel = (action: IngestGapBrandGroup['recommendedAction']) => {
    if (action === 'queue_discovery') return 'Scrape ready';
    if (action === 'enable_scraper') return 'Enable scraper';
    return 'Needs scraper ticket';
  };

  const actionTone = (
    action: IngestGapBrandGroup['recommendedAction']
  ): StatusTone => {
    if (action === 'queue_discovery') return 'positive';
    if (action === 'enable_scraper') return 'caution';
    return 'neutral';
  };

  const getProductListingPath = (record: MapPricingRecord): string => {
    const canonicalId = record.canonicalProductId || '';
    if (canonicalId) {
      return `/app/admin/products?productId=${encodeURIComponent(canonicalId)}`;
    }
    if (record.productStatus === 'bundle_rebate' && record.bundleOffer) {
      return `/app/admin/products?search=${encodeURIComponent(record.bundleOffer)}`;
    }
    // No catalog product yet — still useful to land on products search for this SKU
    return `/app/admin/products?search=${encodeURIComponent(record.model)}`;
  };

  // Create record mutation
  const createMutation = useMutation({
    mutationFn: async (recordData: any) => {
      const response = await fetch(`${getApiUrl()}/map-pricing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`,
        },
        body: JSON.stringify(recordData),
      });
      if (!response.ok) throw new Error('Failed to create MAP pricing record');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mapPricingRecords'] });
      queryClient.invalidateQueries({ queryKey: ['mapPricingStats'] });
      setDialogOpen(false);
      showToast('MAP pricing record created successfully', 'success');
    },
  });

  // Update record mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, recordData }: { id: string; recordData: any }) => {
      const response = await fetch(`${getApiUrl()}/map-pricing/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getAuthToken()}`,
        },
        body: JSON.stringify(recordData),
      });
      if (!response.ok) throw new Error('Failed to update MAP pricing record');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mapPricingRecords'] });
      queryClient.invalidateQueries({ queryKey: ['mapPricingStats'] });
      setDialogOpen(false);
      showToast('MAP pricing record updated successfully', 'success');
    },
  });

  // Delete record mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${getApiUrl()}/map-pricing/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${await getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to delete MAP pricing record');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mapPricingRecords'] });
      queryClient.invalidateQueries({ queryKey: ['mapPricingStats'] });
      setDialogOpen(false);
      showToast('MAP pricing record deleted successfully', 'success');
    },
  });

  const handleOpenDialog = (type: 'create' | 'edit' | 'delete', record?: MapPricingRecord) => {
    setDialogType(type);
    setSelectedRecord(record || null);
    
    if (type === 'edit' && record) {
      setFormData({
        model: record.model || '',
        description: record.description || '',
        brand: record.brand || '',
        category: record.category || '',
        mapPrice: record.mapPrice?.toString() || '',
        surePrice: record.surePrice?.toString() || '',
        sppgPrice: record.sppgPrice?.toString() || '',
        status: record.status || 'active',
      });
    } else if (type === 'create') {
      setFormData({
        model: '',
        description: '',
        brand: 'Sony',
        category: '',
        mapPrice: '',
        surePrice: '',
        sppgPrice: '',
        status: 'active',
      });
    }
    
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRecord(null);
    setFormData({
      model: '',
      description: '',
      brand: '',
      category: '',
      mapPrice: '',
      surePrice: '',
      sppgPrice: '',
      status: 'active',
    });
  };

  const handleSubmit = () => {
    const recordData = {
      ...formData,
      mapPrice: formData.mapPrice ? parseFloat(formData.mapPrice) : null,
      surePrice: formData.surePrice ? parseFloat(formData.surePrice) : null,
      sppgPrice: formData.sppgPrice ? parseFloat(formData.sppgPrice) : null,
    };

    if (dialogType === 'create') {
      createMutation.mutate(recordData);
    } else if (dialogType === 'edit' && selectedRecord?.id) {
      updateMutation.mutate({ id: selectedRecord.id, recordData });
    }
  };

  const handleDelete = () => {
    if (selectedRecord?.id) {
      deleteMutation.mutate(selectedRecord.id);
    }
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'Camera': return <Category />;
      case 'Lens': return <AttachMoney />;
      case 'Accessory': return <Business />;
      case 'Professional Camera': return <TrendingUp />;
      default: return <Category />;
    }
  };

  const records = recordsData?.data || [];
  const totalCount = recordsData?.total ?? recordsData?.count ?? 0;

  return (
    <PageContainer>
      <PageHeader
        title="MAP Pricing"
        description="Manage Minimum Advertised Price (MAP) data for all vendors"
      />

      {/* Statistics Cards */}
      {statsLoading ? (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Skeleton variant="rectangular" height={120} />
            </Box>
          ))}
        </Box>
      ) : stats ? (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoney color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Records</Typography>
                </Box>
                <Typography variant="h4">{formatNumber(stats.totalRecords)}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle color="secondary" sx={{ mr: 1 }} />
                  <Tooltip title="Effective now: the start date has passed and the end date has not.">
                    <Typography variant="h6">Active Records</Typography>
                  </Tooltip>
                </Box>
                <Typography variant="h4">{formatNumber(stats.activeRecords ?? 0)}</Typography>
              </CardContent>
            </Card>
          </Box>
          {stats.upcomingRecords !== undefined && (
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Schedule color="info" sx={{ mr: 1 }} />
                    <Tooltip title="Start date is still in the future. These do not price anything yet.">
                      <Typography variant="h6">Upcoming</Typography>
                    </Tooltip>
                  </Box>
                  <Typography variant="h4">{formatNumber(stats.upcomingRecords)}</Typography>
                </CardContent>
              </Card>
            </Box>
          )}
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Category color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">Models Affected</Typography>
                </Box>
                <Typography variant="h4">{formatNumber(stats.modelsAffected ?? 0)}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUp color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">New Records</Typography>
                </Box>
                <Typography variant="h4">{formatNumber(stats.newRecords ?? 0)}</Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      ) : null}

      {/* Filters | Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', flex: '1 1 520px' }}>
            <TextField
              size="small"
              placeholder="Search by model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: '1 1 200px', minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Camera">Camera</MenuItem>
                <MenuItem value="Lens">Lens</MenuItem>
                <MenuItem value="Accessory">Accessory</MenuItem>
                <MenuItem value="Professional Camera">Professional Camera</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Gap report</InputLabel>
              <Select
                value={gapFilter}
                onChange={(e) => setGapFilter(e.target.value as GapFilter)}
                label="Gap report"
              >
                <MenuItem value="">All records</MenuItem>
                <MenuItem value="needs_ingest">Needs ingest</MenuItem>
                <MenuItem value="missing_cameras">Missing cameras</MenuItem>
                <MenuItem value="bundle_rebates">Bundle rebates</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<FilterList />}
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('');
                setBrandFilter('');
                setGapFilter('');
              }}
            >
              Clear filters
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Assignment />}
              onClick={() => {
                setGapDialogOpen(true);
                setSelectedGapBrands([]);
              }}
            >
              Missing products
            </Button>
            <Tooltip title="CSV of missing catalog SKUs (up to 10,000 per brand; the on-screen list is capped at 200)">
              <span>
                <IconButton
                  aria-label="Download missing products report"
                  onClick={() => void downloadMissingProductsCsv()}
                  disabled={exportingGaps}
                >
                  {exportingGaps ? <CircularProgress size={20} /> : <CloudDownload />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton aria-label="Refresh" onClick={() => refetch()}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog('create')}
            >
              Add record
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Records Table */}
      <Paper>
        <TableContainer>
          <Table sx={{ minWidth: 1000 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 180 }}>Model</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Description</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>MAP Price</TableCell>
                <TableCell>
                  <Tooltip title="Price after instant rebate: the MAP price minus any instant rebate, or the MAP price when there is no rebate. The abbreviation is not defined in the code or docs.">
                    <span>SURE Price</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Effective Date</TableCell>
                <TableCell>Expiration Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recordsLoading ? (
                Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No MAP pricing records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record: MapPricingRecord) => (
                  <TableRow key={record.id} hover>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <MuiLink
                          component={RouterLink}
                          to={getProductListingPath(record)}
                          underline="hover"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            maxWidth: 200,
                            fontWeight: 600,
                            fontFamily: tokens.font.mono,
                          }}
                          title={record.model}
                        >
                          <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {record.model}
                          </Box>
                          <OpenInNew sx={{ fontSize: 14, opacity: 0.7 }} />
                        </MuiLink>
                        {record.needsIngestion && (
                          <Tooltip
                            title={
                              record.productStatus === 'placeholder'
                                ? 'Only a pricing placeholder exists — full product data still needs to be ingested'
                                : record.inferredProductType === 'camera'
                                  ? 'Camera body is not in the catalog yet'
                                  : 'No catalog product found — this SKU needs to be ingested'
                            }
                          >
                            <Chip
                              icon={<CloudDownload sx={{ fontSize: '14px !important' }} />}
                              label={
                                record.inferredProductType === 'camera'
                                  ? 'Missing camera'
                                  : 'Needs ingest'
                              }
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                            />
                          </Tooltip>
                        )}
                        {record.productStatus === 'bundle_rebate' && (
                          <Tooltip
                            title={
                              record.bundleCondition
                                ? `Bundle rebate: ${record.bundleOffer || record.model} when sold with ${record.bundleCondition}`
                                : 'Conditional sold-with rebate — not a missing catalog SKU'
                            }
                          >
                            <Chip
                              icon={<LocalOffer sx={{ fontSize: '14px !important' }} />}
                              label="Bundle rebate"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={record.description || ''}>
                        <Typography
                          variant="body2"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {record.description}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.brand || 'Unknown'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getCategoryIcon(record.category)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {record.category}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {record.mapPrice ? formatCurrency(record.mapPrice) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {record.surePrice ? formatCurrency(record.surePrice) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {(() => {
                          const date = record.effectiveDate || record.startDate;
                          if (!date) return '-';
                          if (typeof date === 'string') return new Date(date).toLocaleDateString();
                          if (date._seconds) return new Date(date._seconds * 1000).toLocaleDateString();
                          return '-';
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {(() => {
                          const date = record.endDate || record.expirationDate;
                          if (!date) return '-';
                          if (typeof date === 'string') return new Date(date).toLocaleDateString();
                          if (date._seconds) return new Date(date._seconds * 1000).toLocaleDateString();
                          return '-';
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                        <StatusChip
                          label={statusLabel(record.status)}
                          size="small"
                          tone={statusTone(record.status)}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog('edit', record)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog('delete', record)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      {/* Dialog */}
      <FormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={dialogType === 'delete' ? handleDelete : handleSubmit}
        title={
          dialogType === 'create'
            ? 'Create MAP Pricing Record'
            : dialogType === 'edit'
              ? 'Edit MAP Pricing Record'
              : 'Delete MAP Pricing Record'
        }
        submitLabel={
          dialogType === 'delete'
            ? (deleteMutation.isPending ? 'Deleting…' : 'Delete')
            : (createMutation.isPending || updateMutation.isPending)
              ? 'Saving…'
              : dialogType === 'create' ? 'Create' : 'Update'
        }
        submitColor={dialogType === 'delete' ? 'error' : 'primary'}
        submitDisabled={
          dialogType === 'delete'
            ? deleteMutation.isPending
            : createMutation.isPending || updateMutation.isPending
        }
        maxWidth="md"
      >
          {dialogType === 'delete' ? (
            <Alert severity="warning">
              Are you sure you want to delete the MAP pricing record for "{selectedRecord?.model}"?
              This action cannot be undone.
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  fullWidth
                  required
                />
                <BrandSelect
                  label="Brand"
                  value={formData.brand}
                  onChange={(brand) => setFormData({ ...formData, brand })}
                  sx={{ width: '100%' }}
                />
              </Stack>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    label="Category"
                  >
                    <MenuItem value="Camera">Camera</MenuItem>
                    <MenuItem value="Lens">Lens</MenuItem>
                    <MenuItem value="Accessory">Accessory</MenuItem>
                    <MenuItem value="Professional Camera">Professional Camera</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <NumberField
                  label="MAP Price"
                  min={0}
                  step={0.01}
                  adornment="$"
                  value={formData.mapPrice}
                  onChange={(value) => setFormData({ ...formData, mapPrice: value })}
                  size="medium"
                  sx={{ width: '100%' }}
                />
                <NumberField
                  label="SURE Price"
                  min={0}
                  step={0.01}
                  adornment="$"
                  value={formData.surePrice}
                  onChange={(value) => setFormData({ ...formData, surePrice: value })}
                  size="medium"
                  sx={{ width: '100%' }}
                />
                <NumberField
                  label="SPPG Price"
                  min={0}
                  step={0.01}
                  adornment="$"
                  value={formData.sppgPrice}
                  onChange={(value) => setFormData({ ...formData, sppgPrice: value })}
                  size="medium"
                  sx={{ width: '100%' }}
                />
              </Stack>
            </Stack>
          )}
      </FormDialog>

      {/* Ingest-gap remediation */}
      <Dialog
        open={gapDialogOpen}
        onClose={() => setGapDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={gapDialogFullScreen}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box>
              <Typography variant="h6">Missing product report</Typography>
              <Typography variant="body2" color="text.secondary">
                MAP and rebate rows with no catalog product. Use this to queue discovery or decide
                which brands need a scraper. Bundle rebates (“sold with any Z series…”) are
                conditional offers, not missing SKUs. The lists below cap at 200 SKUs per brand;
                Download CSV includes up to 10,000.
              </Typography>
            </Box>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={() => refetchGapReport()}
              disabled={gapReportFetching}
            >
              Rebuild
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {gapReportLoading || gapReportFetching ? (
            <LoadingState />
          ) : !gapReport || (
            (gapReport.groups || []).length === 0 &&
            (gapReport.missingCameraGroups || []).length === 0 &&
            (gapReport.bundleRebateGroups || []).length === 0
          ) ? (
            <Alert severity="success">
              No ingest gaps or bundle rebate rows found in this scan.
            </Alert>
          ) : (
            <Box>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={`${gapReport.totalMissingCameras ?? 0} missing cameras`}
                  color="warning"
                />
                <Chip label={`${gapReport.totalNeedingIngest} SKUs need ingest`} color="warning" variant="outlined" />
                <Chip
                  label={`${gapReport.totalBundleRebates ?? 0} bundle rebates`}
                  color="info"
                  variant="outlined"
                />
                <Chip
                  label={`${gapReport.brandsWithScraperReady} brands scrape-ready`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  label={`${gapReport.brandsNeedingScraper} brands need scraper`}
                  variant="outlined"
                />
                <Chip
                  label={`Scanned ${gapReport.totalPricingRecordsScanned} pricing rows`}
                  size="small"
                  variant="outlined"
                />
                {gapReport.truncated && (
                  <Chip
                    label="Scan truncated — some older rows may be missing"
                    color="warning"
                    size="small"
                  />
                )}
              </Stack>

              <Tabs
                value={gapDialogTab}
                onChange={(_event, value: GapDialogTab) => setGapDialogTab(value)}
                sx={{ mb: 2 }}
              >
                <Tab
                  value="cameras"
                  label={`Missing cameras (${gapReport.totalMissingCameras ?? 0})`}
                />
                <Tab
                  value="catalog"
                  label={`Catalog gaps (${gapReport.totalNeedingIngest})`}
                />
                <Tab
                  value="bundles"
                  label={`Bundle rebates (${gapReport.totalBundleRebates ?? 0})`}
                />
              </Tabs>

              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={exportingGaps ? <CircularProgress size={16} /> : <CloudDownload />}
                  onClick={() => void downloadMissingProductsCsv()}
                  disabled={exportingGaps}
                  title="CSV includes up to 10,000 SKUs per brand; this dialog lists at most 200"
                >
                  Download CSV
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  On-screen lists cap at 200 SKUs per brand. The CSV includes up to 10,000.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={() => copyText(gapReport.aiBriefMarkdown, 'AI brief copied')}
                >
                  Copy AI brief
                </Button>
                {gapDialogTab === 'bundles' ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="info"
                    startIcon={<ContentCopy />}
                    disabled={!gapReport.bundleTicketDraft}
                    onClick={() => {
                      const draft = gapReport.bundleTicketDraft;
                      if (!draft) return;
                      copyText(`${draft.title}\n\n${draft.body}`, 'Bundle rebate ticket copied');
                    }}
                  >
                    Copy bundle Linear ticket
                  </Button>
                ) : (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      startIcon={queueDiscoveryMutation.isPending ? <CircularProgress size={16} /> : <PlayArrow />}
                      disabled={
                        queueDiscoveryMutation.isPending ||
                        selectedGapBrands.length === 0
                      }
                      onClick={() => queueDiscoveryMutation.mutate(selectedGapBrands)}
                    >
                      Queue discovery ({selectedGapBrands.length})
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        const source =
                          gapDialogTab === 'cameras'
                            ? gapReport.missingCameraGroups || []
                            : gapReport.groups || [];
                        const ready = source
                          .filter((g) => g.recommendedAction === 'queue_discovery' && g.brandId)
                          .map((g) => g.brandId!) as string[];
                        setSelectedGapBrands(ready);
                      }}
                    >
                      Select all scrape-ready
                    </Button>
                  </>
                )}
              </Stack>

              {gapDialogTab === 'bundles' ? (
                (gapReport.bundleRebateGroups || []).length === 0 ? (
                  <Alert severity="success">No sold-with bundle rebate rows in this scan.</Alert>
                ) : (
                  (gapReport.bundleRebateGroups || []).map((group) => (
                    <Accordion key={`bundle-${group.brand}-${group.brandId || 'none'}`} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          useFlexGap
                          sx={{ flexWrap: 'wrap', width: '100%', pr: 1 }}
                        >
                          <Typography fontWeight={600}>{group.brand}</Typography>
                          <Chip size="small" color="info" label={`${group.skuCount} offers`} />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} useFlexGap>
                          <Button
                            size="small"
                            startIcon={<ContentCopy />}
                            onClick={() =>
                              copyText(
                                `${group.ticketDraft.title}\n\n${group.ticketDraft.body}`,
                                `Bundle ticket for ${group.brand} copied`
                              )
                            }
                          >
                            Copy Linear ticket
                          </Button>
                          <Button
                            size="small"
                            component="a"
                            href="https://linear.app/motzu/team/MOT/new"
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                          >
                            Open Linear
                          </Button>
                        </Stack>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Offer</TableCell>
                                <TableCell>Sold with</TableCell>
                                <TableCell align="right">MAP</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.skus.map((sku) => (
                                <TableRow key={sku.pricingId}>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {sku.offerProduct || sku.model}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>
                                      {sku.condition || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {sku.mapPrice != null ? formatCurrency(Number(sku.mapPrice)) : '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {group.skuCount > group.skus.length && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Showing {group.skus.length} of {group.skuCount} offers
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))
                )
              ) : (
                (() => {
                  const dialogGroups =
                    gapDialogTab === 'cameras'
                      ? gapReport.missingCameraGroups || []
                      : gapReport.groups || [];
                  if (dialogGroups.length === 0) {
                    return (
                      <Alert severity="success">
                        {gapDialogTab === 'cameras'
                          ? 'No missing camera bodies in this scan.'
                          : 'No catalog ingest gaps — every pricing SKU has a canonical product (bundle rebates excluded).'}
                      </Alert>
                    );
                  }
                  return dialogGroups.map((group) => {
                const canQueue = !!group.brandId && group.scraperImplemented;
                const checked = !!group.brandId && selectedGapBrands.includes(group.brandId);
                return (
                  <Accordion key={`${gapDialogTab}-${group.brand}-${group.brandId || 'none'}`} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        useFlexGap
                        sx={{ flexWrap: 'wrap', width: '100%', pr: 1 }}
                      >
                        {canQueue && (
                          <Checkbox
                            size="small"
                            checked={checked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              if (!group.brandId) return;
                              setSelectedGapBrands((prev) =>
                                e.target.checked
                                  ? Array.from(new Set([...prev, group.brandId!]))
                                  : prev.filter((id) => id !== group.brandId)
                              );
                            }}
                          />
                        )}
                        <Typography fontWeight={600}>{group.brand}</Typography>
                        <Chip
                          size="small"
                          label={
                            gapDialogTab === 'cameras'
                              ? `${group.skuCount} cameras`
                              : `${group.skuCount} SKUs`
                          }
                        />
                        <StatusChip
                          size="small"
                          label={actionLabel(group.recommendedAction)}
                          tone={actionTone(group.recommendedAction)}
                          variant="outlined"
                        />
                        {group.brandId && (
                          <Typography variant="caption" color="text.secondary">
                            {group.brandId}
                          </Typography>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} useFlexGap>
                        <Button
                          size="small"
                          startIcon={<ContentCopy />}
                          onClick={() =>
                            copyText(
                              `${group.ticketDraft.title}\n\n${group.ticketDraft.body}`,
                              `Ticket draft for ${group.brand} copied`
                            )
                          }
                        >
                          Copy Linear ticket
                        </Button>
                        {group.recommendedAction === 'file_scraper_ticket' && (
                          <Button
                            size="small"
                            component="a"
                            href="https://linear.app/motzu/team/MOT/new"
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                          >
                            Open Linear
                          </Button>
                        )}
                      </Stack>

                      {group.scraperImplemented && group.brandId && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Paste product URLs to scrape
                          </Typography>
                          <ChipInput
                            aria-label="Paste product URLs to scrape"
                            value={splitProductUrls(gapUrlDrafts[group.brandId] || '')}
                            onChange={(next) =>
                              setGapUrlDrafts((prev) => ({
                                ...prev,
                                [group.brandId!]: next.join('\n'),
                              }))
                            }
                            validate={(url) => validateUrl(url)}
                            placeholder="https://…/product/sku-1"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Newline or comma separated. Scrapes only these PDPs for this brand.
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ mt: 1 }}
                            startIcon={
                              scrapeGapUrlsMutation.isPending ? (
                                <CircularProgress size={14} />
                              ) : (
                                <PlayArrow />
                              )
                            }
                            disabled={
                              scrapeGapUrlsMutation.isPending ||
                              parseProductUrls(gapUrlDrafts[group.brandId] || '').length === 0
                            }
                            onClick={() => {
                              const urls = parseProductUrls(gapUrlDrafts[group.brandId!] || '');
                              if (!urls.length || !group.brandId) return;
                              scrapeGapUrlsMutation.mutate({ brandId: group.brandId, urls });
                            }}
                          >
                            Scrape pasted URLs
                          </Button>
                        </Box>
                      )}

                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Model</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell align="right">MAP</TableCell>
                              <TableCell>Gap</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.skus.map((sku) => (
                              <TableRow key={sku.pricingId}>
                                <TableCell>
                                  <Typography variant="body2" fontFamily="monospace">
                                    {sku.model}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                                    {sku.description || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {sku.mapPrice != null ? formatCurrency(Number(sku.mapPrice)) : '—'}
                                </TableCell>
                                <TableCell>
                                  <Chip size="small" label={sku.productType || sku.productStatus} variant="outlined" />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {group.skuCount > group.skus.length && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Showing {group.skus.length} of {group.skuCount} SKUs
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
                  });
                })()
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGapDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </PageContainer>
  );
};
